// components/views/profile/QuickBooksSetup.jsx
import React, { Component } from 'react';
import QboAPI from '../../../services/qboService';

class QuickBooksSetup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: null
    };
  }

  handleConnect = async () => {
    this.setState({ loading: true, error: null });
    try {
      const response = await QboAPI.getAuthUri(this.props.installer.Installer_id);
      if (response.authUri) {
        window.location.href = response.authUri;
      } else {
        throw new Error('Failed to get QuickBooks authorization link');
      }
    } catch (error) {
      console.error('QBO connect error:', error);
      this.setState({
        loading: false,
        error: error.response?.data?.message || 'Failed to connect to QuickBooks'
      });
    }
  };

  render() {
    const { installer, qbo } = this.props;
    const { loading, error } = this.state;

    if (!installer) {
      return (
        <div className="alert alert-warning">
          <p className="mb-0">Unable to load account information. Please refresh the page.</p>
        </div>
      );
    }

    const isConnected = qbo && qbo.connected && !qbo.token_expired;
    const isActive = isConnected && qbo.status === 'active';
    const isExpired = qbo && qbo.connected && qbo.token_expired;

    // ── Connected & Active ───────────────────────────────────────────
    if (isConnected && isActive) {
      return (
        <div className="qbo-setup-complete">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <span className="badge badge-success mr-2">
                <i className="fa-sharp fa-regular fa-check mr-1"></i> Connected
              </span>
              <span className="text-muted">Your QuickBooks account is linked and syncing</span>
            </div>
          </div>

          {qbo.last_sync_at && (
            <div className="mt-2">
              <small className="text-muted">
                <i className="fa-sharp fa-regular fa-clock mr-1"></i>
                Last synced: {new Date(qbo.last_sync_at).toLocaleString()}
              </small>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mt-2 mb-0">
              <small>{error}</small>
            </div>
          )}
        </div>
      );
    }

    // ── Connected but not active — needs account mapping ─────────────
    if (isConnected && !isActive) {
      return (
        <div className="qbo-setup-required">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <span className="badge badge-warning mr-2">
                <i className="fa-sharp fa-regular fa-link mr-1"></i> Connected
              </span>
              <span className="text-muted">Account mapping required before sync can begin</span>
            </div>
          </div>

          <div className="alert alert-warning mt-3 mb-0">
            <div className="d-flex align-items-center">
              <i className="fa-sharp fa-regular fa-gear mr-3" style={{ fontSize: '1.5rem' }}></i>
              <div className="flex-grow-1">
                <strong>Configure Your Accounts</strong>
                <p className="mb-0 small">Map your QuickBooks chart of accounts and assign bank accounts to activate sync.</p>
              </div>
              <button
                className="btn btn-warning btn-sm ml-3"
                onClick={() => { if (this.props.onSetupClick) this.props.onSetupClick(); }}
              >
                <i className="fa-sharp fa-regular fa-arrow-right mr-1"></i>
                Configure Accounts
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mt-2 mb-0">
              <small>{error}</small>
            </div>
          )}
        </div>
      );
    }

    // ── Not Connected (default — includes expired tokens) ────────────
    return (
      <div className="qbo-setup-required">
        {isExpired && (
          <div className="alert alert-warning mb-3">
            <div className="d-flex align-items-center">
              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
              <div>
                <strong>Connection Expired</strong> — Your QuickBooks authorization has expired. Please reconnect to resume syncing.
              </div>
            </div>
          </div>
        )}

        <div className="alert alert-info mb-3">
          <div className="d-flex align-items-center">
            <div className="mr-3">
              <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                <i className="fa-sharp fa-regular fa-link"></i>
              </div>
            </div>
            <div>
              <h6 className="alert-heading mb-1">{isExpired ? 'Reconnect Your QuickBooks Account' : 'Connect Your QuickBooks Account'}</h6>
              <p className="mb-0">Link your QuickBooks Online account to automatically sync invoices and payments.</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <small>{error}</small>
          </div>
        )}

        <div className="row mb-3">
          <div className="col-md-6">
            <h6>What Gets Synced</h6>
            <ul className="list-unstyled">
              <li><i className="fa-sharp fa-regular fa-check text-success mr-1"></i> Invoices</li>
              <li><i className="fa-sharp fa-regular fa-check text-success mr-1"></i> Payments received</li>
              <li><i className="fa-sharp fa-regular fa-check text-success mr-1"></i> Customer records</li>
            </ul>
          </div>
          <div className="col-md-6">
            <h6>How It Works</h6>
            <ul className="list-unstyled">
              <li><i className="fa-sharp fa-regular fa-shield-check text-primary mr-1"></i> Secure OAuth connection</li>
              <li><i className="fa-sharp fa-regular fa-arrows-rotate text-primary mr-1"></i> Automatic sync on payment</li>
              <li><i className="fa-sharp fa-regular fa-plug text-primary mr-1"></i> Disconnect anytime</li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <button
            className="btn btn-success btn-lg"
            onClick={this.handleConnect}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                Connecting...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-link mr-2"></i>
                {isExpired ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
              </>
            )}
          </button>
          <p className="text-muted mt-2 small">
            You'll be redirected to Intuit to authorize access
          </p>
        </div>
      </div>
    );
  }
}

export default QuickBooksSetup;
